import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faDownload, faEye, faCalendarAlt, faDollarSign, faCheckCircle, faClock, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { subscriptionAPI } from '../../services/api';

const BillingSettings: React.FC = () => {
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('settings');
  const { business } = useAuth();
  const [invoices, setInvoices] = useState([
    {
      id: 'INV-2024-001',
      date: '2024-01-15',
      amount: 99.99,
      status: 'paid',
      plan: 'Pro Plan',
      period: 'Ocak 2024',
      downloadUrl: '#'
    },
    {
      id: 'INV-2024-002',
      date: '2024-02-15',
      amount: 99.99,
      status: 'paid',
      plan: 'Pro Plan',
      period: 'Şubat 2024',
      downloadUrl: '#'
    },
    {
      id: 'INV-2024-003',
      date: '2024-03-15',
      amount: 99.99,
      status: 'pending',
      plan: 'Pro Plan',
      period: 'Mart 2024',
      downloadUrl: '#'
    },
    {
      id: 'INV-2023-012',
      date: '2023-12-15',
      amount: 49.99,
      status: 'paid',
      plan: 'Basic Plan',
      period: 'Aralık 2023',
      downloadUrl: '#'
    }
  ]);

  const [currentPlan, setCurrentPlan] = useState({
    name: '',
    price: 0,
    period: 'aylık',
    features: [] as string[],
    nextBilling: ''
  });
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Fetch subscription data
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!business?._id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await subscriptionAPI.getSubscription(business._id);
        if (response.data?.subscription) {
          setSubscription(response.data.subscription);
          // You can map subscription data to currentPlan here
          // For now, we'll use business.packageId if available
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [business?._id]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-green-400" />;
      case 'pending':
        return <FontAwesomeIcon icon={faClock} className="text-yellow-400" />;
      case 'failed':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-400" />;
      default:
        return <FontAwesomeIcon icon={faClock} className="text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid':
        return t('billingSettings.statusPaid');
      case 'pending':
        return t('billingSettings.statusPending');
      case 'failed':
        return t('billingSettings.statusFailed');
      default:
        return t('billingSettings.statusUnknown');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'text-green-400 bg-green-500/20 border-green-400/30';
      case 'pending':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-400/30';
      case 'failed':
        return 'text-red-400 bg-red-500/20 border-red-400/30';
      default:
        return 'text-gray-400 bg-gray-500/20 border-gray-400/30';
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('billingSettings.title')}</h2>
        <p className="text-white/70">{t('billingSettings.description')}</p>
      </div>

      {/* Current Plan */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{t('billingSettings.currentPlan')}</h3>
          <button 
            onClick={() => {
              const currentPath = window.location.pathname;
              const lang = currentPath.split('/')[1] || 'tr';
              window.location.href = `/${lang}/packages`;
            }}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-300"
          >
            {t('billingSettings.changePlan')}
          </button>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-white/70">{t('billingSettings.loading')}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-2xl font-bold text-white mb-1">
                {subscription?.name || business?.packageId || t('billingSettings.noPlan')}
              </h4>
              {subscription && (
                <p className="text-white/60">
                  {subscription.status === 'active' ? t('billingSettings.active') : t('billingSettings.inactive')}
                </p>
              )}
            </div>
            <div>
              <p className="text-white/60 text-sm mb-1">{t('billingSettings.nextBilling')}</p>
              <p className="text-white font-semibold">
                {subscription?.nextPaymentAt 
                  ? new Date(subscription.nextPaymentAt).toLocaleDateString('tr-TR')
                  : t('billingSettings.noBillingDate')
                }
              </p>
            </div>
            <div>
              <p className="text-white/60 text-sm mb-1">{t('billingSettings.subscriptionStatus')}</p>
              <p className="text-white font-semibold">
                {subscription?.status 
                  ? subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)
                  : t('billingSettings.noSubscription')
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Invoice History */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">{t('billingSettings.invoiceHistory')}</h3>
          <div className="flex items-center space-x-4">
            <select className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
              <option value="all">{t('billingSettings.allInvoices')}</option>
              <option value="paid">{t('billingSettings.paid')}</option>
              <option value="pending">{t('billingSettings.pending')}</option>
            </select>
          </div>
        </div>

        <div className="space-y-4">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
                    <FontAwesomeIcon icon={faFileInvoice} className="text-white text-lg" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-3 mb-1">
                      <h4 className="text-white font-semibold">{invoice.id}</h4>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(invoice.status)}`}>
                        <span className="flex items-center space-x-1">
                          {getStatusIcon(invoice.status)}
                          <span>{getStatusText(invoice.status)}</span>
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-white/60 text-sm">
                      <div className="flex items-center space-x-1">
                        <FontAwesomeIcon icon={faCalendarAlt} className="text-xs" />
                        <span>{new Date(invoice.date).toLocaleDateString('tr-TR')}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FontAwesomeIcon icon={faDollarSign} className="text-xs" />
                        <span>${invoice.amount}</span>
                      </div>
                      <span>{invoice.plan}</span>
                    </div>
                    <p className="text-white/50 text-xs mt-1">{invoice.period}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                    <FontAwesomeIcon icon={faEye} className="text-sm" />
                  </button>
                  <button className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                    <FontAwesomeIcon icon={faDownload} className="text-sm" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Billing Information */}
      <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">{t('billingSettings.billingInformation')}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-white font-semibold mb-3">{t('billingSettings.billingAddress')}</h4>
            <div className="space-y-2 text-white/70 text-sm">
              {business?.name && <p>{business.name}</p>}
              {business?.location?.address && <p>{business.location.address}</p>}
              {business?.location?.city && business?.location?.country && (
                <p>{business.location.city}, {business.location.country}</p>
              )}
              {!business?.location && (
                <p className="text-white/50">{t('billingSettings.noAddress')}</p>
              )}
            </div>
            <button 
              onClick={() => {
                // Navigate to business profile settings to edit address
                const currentPath = window.location.pathname;
                const lang = currentPath.split('/')[1] || 'tr';
                window.location.href = `/${lang}/settings?tab=business`;
              }}
              className="mt-3 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors text-sm"
            >
              {t('billingSettings.editAddress')}
            </button>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-3">{t('billingSettings.businessInfo')}</h4>
            <div className="space-y-2 text-white/70 text-sm">
              {business?.email && (
                <p><span className="text-white/50">{t('billingSettings.email')}:</span> {business.email}</p>
              )}
              {business?.phone && (
                <p><span className="text-white/50">{t('billingSettings.phone')}:</span> {business.phone}</p>
              )}
              {business?.category && (
                <p><span className="text-white/50">{t('billingSettings.category')}:</span> {business.category}</p>
              )}
            </div>
            <button 
              onClick={() => {
                // Navigate to business profile settings
                const currentPath = window.location.pathname;
                const lang = currentPath.split('/')[1] || 'tr';
                window.location.href = `/${lang}/settings?tab=business`;
              }}
              className="mt-3 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors text-sm"
            >
              {t('billingSettings.editBusinessInfo')}
            </button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-8 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-400/20 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-white mb-2">Fatura ile İlgili Yardım</h4>
        <p className="text-white/70 text-sm mb-4">
          Faturalarınızla ilgili sorularınız mı var? Size yardımcı olmaktan mutluluk duyarız.
        </p>
        <div className="flex flex-wrap gap-3">
          <button className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors text-sm">
            SSS'yi Görüntüle
          </button>
          <button className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors text-sm">
            Destek Ekibiyle İletişime Geç
          </button>
          <button className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors text-sm">
            Fatura Talebi Gönder
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingSettings;
